// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  AltoSpinningLoader,
  Description,
  FlatCard,
  Icon,
  MdPadding,
  Pressable,
  Row,
  Separator,
  XsSpacing,
} from '@alto/design-system';
import React, { useContext, useEffect } from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { getHasScheduleableDeliveryFeesInCart } from '~shared/features/checkout/selectors/getCheckout';
import { getOrderAddressId, getOrderDate } from '~shared/features/checkout/selectors/getOrder';
import { type PaymentMethodWithAmount } from '~shared/features/payments/types';
import { getAutobillPrescriptionLoadingByID } from '~shared/features/pricing/selectors/getPricing';
import { getPrescriptionsForShipment } from '~shared/features/shipments/selectors/getPrescriptionsForShipment';
import { isDeliveryOrMailFeeLineItem, isItemTypeExpeditedDeliveryFee } from '~shared/helpers/pricingBreakdown';
import { usePrevious } from '~shared/hooks';
import { useOrderPricing } from '~shared/queries';
import { useSelectorShared } from '~shared/store';
import { type PaymentMethod, type Shipment } from '~shared/types';
import { PricingBreakdownActionSheet } from '../../pricing-breakdown';
import { PricingInfoLineItem } from '../../pricing-info';
import { PaymentMethodInfo } from './PaymentMethodInfo';

export type Props = {
  readonly origin: 'Cart' | 'Checkout' | 'DeliveryDetails';
  readonly openEditTip?: () => void;
  readonly shipmentPaymentMethods?: PaymentMethodWithAmount[] | null;
  readonly showZeroTipLineItem?: boolean;
  readonly showEditTipOption?: boolean;
  readonly showEstimatedTotal?: boolean;
  readonly tipPaymentMethod?: PaymentMethod | null | undefined;
  readonly shipmentID?: number;
  readonly shipment?: Shipment;
};

export const PaymentSummary = ({
  openEditTip,
  shipmentPaymentMethods = null,
  showZeroTipLineItem = false,
  showEditTipOption = false,
  showEstimatedTotal,
  tipPaymentMethod,
  origin,
  shipmentID,
  shipment,
}: Props) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { isOrderPricingLoading, orderPricing, refetchOrderPricing } = useOrderPricing({ shipmentID });
  const autobillLoadingByID = useSelectorShared(getAutobillPrescriptionLoadingByID);
  const anyAutobillLoading = Object.values(autobillLoadingByID).some((v) => v);
  const prevAutobillLoading = usePrevious(anyAutobillLoading);
  const prescriptionsForShipment = useSelectorShared((state) => getPrescriptionsForShipment(state, { shipmentID }));
  const { prescriptions: prescriptionsInCart } = usePrescriptionsInCart();
  const prescriptions = shipmentID ? prescriptionsForShipment : prescriptionsInCart;
  const itemCount = prescriptions.length;
  const orderDate = useSelectorShared(getOrderDate);
  const addressID = useSelectorShared(getOrderAddressId);
  const hasScheduleableDeliveryFeesInCart = useSelectorShared(getHasScheduleableDeliveryFeesInCart);
  const isCartOrCheckout = ['Cart', 'Checkout'].includes(origin);

  const { line_items } = orderPricing ?? { line_items: [] };
  const getTipAmount = (): number => {
    const tipLineItems = line_items.filter(({ key }) => key === 'tip_amount');
    return tipLineItems[0]?.value || 0;
  };
  const tipAmount = getTipAmount();

  const handleOnPress = () => {
    setActiveActionSheet(
      <PricingBreakdownActionSheet
        origin={origin}
        prescriptions={prescriptions}
        shipmentID={shipmentID}
      />,
    );
  };

  const showSeparator = !!shipmentPaymentMethods;
  const displayPayAtPickup = shipment && shipment.is_pickup && shipment.pay_at_pickup;

  useEffect(() => {
    // if any of the prescriptions were still autobilling and now none are, refetch order pricing to get updated prices
    if (prevAutobillLoading && !anyAutobillLoading) {
      refetchOrderPricing();
    }
  }, [anyAutobillLoading, prevAutobillLoading, refetchOrderPricing]);

  return (
    <FlatCard>
      <MdPadding
        topPadding={SPACING.STATIC.SM}
        bottomPadding={showSeparator ? SPACING.STATIC.XXS : SPACING.STATIC.SM}
      >
        {isOrderPricingLoading ? (
          <Row center>
            <AltoSpinningLoader />
          </Row>
        ) : (
          line_items.map(({ key: itemType, label, original_value: originalValue, value, info_key }) => {
            if (itemType === 'tip_amount' && !showZeroTipLineItem && !tipAmount) return null;

            // Skip hidden delivery fee line items so their trailing XsSpacing doesn't create phantom gaps
            const isDeliveryFeeItem = isDeliveryOrMailFeeLineItem(itemType);
            const isScheduleableFeeItem = isItemTypeExpeditedDeliveryFee(itemType);
            const shouldHideDeliveryFee =
              isCartOrCheckout &&
              isDeliveryFeeItem &&
              (!addressID || !orderDate || (isScheduleableFeeItem && !hasScheduleableDeliveryFeesInCart));
            if (shouldHideDeliveryFee) return null;

            const hasPaymentMethod = !!shipmentPaymentMethods && shipmentPaymentMethods.length > 0;
            const isTotalLineItem = itemType === 'total';

            return (
              <React.Fragment key={itemType}>
                <PricingInfoLineItem
                  infoKey={info_key}
                  itemType={itemType}
                  itemValue={value}
                  label={label}
                  openEditTip={openEditTip}
                  originalValue={originalValue}
                  showEditTipOption={showEditTipOption}
                  showEstimatedTotal={showEstimatedTotal}
                  totalItemCount={itemCount}
                  origin={origin}
                />
                {isTotalLineItem && !hasPaymentMethod ? null : <XsSpacing />}
              </React.Fragment>
            );
          })
        )}
      </MdPadding>
      {showSeparator ? <Separator /> : null}
      {displayPayAtPickup ? (
        <MdPadding
          topPadding={SPACING.STATIC.SM}
          bottomPadding={SPACING.STATIC.NONE}
        >
          <Row centerVertically>
            <Description color={COLORS.TEXT_COLORS.GREY}>Paying at pickup</Description>
          </Row>
        </MdPadding>
      ) : (
        (!!shipmentPaymentMethods || !!tipPaymentMethod) && (
          <MdPadding
            topPadding={SPACING.STATIC.SM}
            bottomPadding={SPACING.STATIC.NONE}
          >
            <PaymentMethodInfo
              shipmentPaymentMethods={shipmentPaymentMethods ?? []}
              showTipPaymentMethod={!!tipAmount}
              tipPaymentMethod={tipPaymentMethod}
            />
          </MdPadding>
        )
      )}
      {isOrderPricingLoading ? null : (
        <Pressable onPress={handleOnPress}>
          <MdPadding
            topPadding={SPACING.STATIC.SM}
            bottomPadding={SPACING.STATIC.SM}
          >
            <Row
              centerVertically
              spaceBetween
            >
              <Description
                color={COLORS.TEXT_COLORS.SECONDARY_DARK}
                fontFamily="semibold"
              >
                Full pricing details
              </Description>
              <Icon
                type="secondary"
                name="chevronright-small"
              />
            </Row>
          </MdPadding>
        </Pressable>
      )}
    </FlatCard>
  );
};
